/**
 * Chatbot Grid — Chatbase-inspired enterprise design
 *
 * Large visual cards with gradient preview thumbnails showing a
 * mini chat-widget mockup. Clean bottom section: name + meta + 3-dot menu.
 */

import React, { useState } from 'react'
import {
  Bot,
  Search,
  MoreHorizontal,
  MessageSquare,
  Database,
  Wrench,
  BarChart3,
  Settings,
  Trash2,
  ExternalLink,
} from 'lucide-react'
import { Card } from '../ui/Card'
import { Input } from '../ui/Input'
import { Skeleton } from '../ui/Skeleton'
import { DeleteConfirmDialog } from '../ui/DeleteConfirmDialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/ShadcnDropdownMenu'
import { useChatbotStore, useChatbots } from '../../stores/chatbotStore'
import type { Chatbot } from '../../types'
import { logger } from '../../utils/logger'
import { useNavigate } from 'react-router-dom'

// ── Props ─────────────────────────────────
interface ChatbotGridProps {
  limit?: number
  showControls?: boolean
  showActions?: boolean
  variant?: 'grid' | 'list'
  onEdit?: (chatbot: Chatbot) => void
  onDelete?: (chatbot: Chatbot) => void
  onTest?: (chatbot: Chatbot) => void
  onTrain?: (chatbot: Chatbot) => void
  onTools?: (chatbot: Chatbot) => void
  onStats?: (chatbot: Chatbot) => void
}

// ── Gradient palettes ─────────────────────
const GRADIENTS = [
  { bg: 'from-blue-400 via-blue-500 to-indigo-600', accent: 'bg-blue-600' },
  { bg: 'from-violet-400 via-purple-500 to-indigo-600', accent: 'bg-violet-600' },
  { bg: 'from-emerald-400 via-teal-500 to-cyan-600', accent: 'bg-emerald-600' },
  { bg: 'from-rose-400 via-pink-500 to-fuchsia-600', accent: 'bg-rose-600' },
  { bg: 'from-amber-400 via-orange-500 to-red-500', accent: 'bg-amber-600' },
  { bg: 'from-cyan-400 via-sky-500 to-blue-600', accent: 'bg-cyan-600' },
  { bg: 'from-fuchsia-400 via-pink-500 to-rose-600', accent: 'bg-fuchsia-600' },
  { bg: 'from-indigo-400 via-blue-500 to-violet-600', accent: 'bg-indigo-600' },
]

function pickGradient(name: string) {
  let h = 0
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h)
  return GRADIENTS[Math.abs(h) % GRADIENTS.length]
}

function modelLabel(model?: string): string {
  if (!model) return 'Default'
  const n = model.split('/').pop() || model
  return n
    .replace('gpt-', 'GPT-')
    .replace('-mini', ' Mini')
    .replace('-turbo', ' Turbo')
    .replace('gemini-', 'Gemini ')
    .replace('claude-', 'Claude ')
}

function relativeDate(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}d ago`
  return `${Math.floor(d / 30)}mo ago`
}

// ── Mini widget preview mockup ────────────
const WidgetPreview: React.FC<{ name: string; accent: string }> = ({ name, accent }) => (
  <div className="absolute top-6 left-1/2 -translate-x-1/2 w-[65%] max-w-[220px]">
    {/* Mini card frame */}
    <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="px-3.5 py-2.5 border-b border-zinc-100 dark:border-zinc-800">
        <p className="text-xs font-semibold text-zinc-800 dark:text-zinc-200 truncate">{name}</p>
      </div>
      {/* Chat body */}
      <div className="px-3.5 py-3 space-y-2.5">
        {/* Bot bubble */}
        <div className="flex gap-1.5">
          <div className="w-4 h-4 rounded-full bg-zinc-200 dark:bg-zinc-700 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <div className="h-2 w-24 bg-zinc-200 dark:bg-zinc-700 rounded-full" />
            <div className="h-2 w-16 bg-zinc-100 dark:bg-zinc-800 rounded-full" />
          </div>
        </div>
        {/* User bubble */}
        <div className="flex justify-end">
          <div className={`h-6 w-28 ${accent} rounded-full opacity-90`} />
        </div>
        {/* Bot reply */}
        <div className="flex gap-1.5">
          <div className="w-4 h-4 rounded-full bg-zinc-200 dark:bg-zinc-700 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <div className="h-2 w-20 bg-zinc-200 dark:bg-zinc-700 rounded-full" />
            <div className="h-2 w-28 bg-zinc-100 dark:bg-zinc-800 rounded-full" />
            <div className="h-2 w-14 bg-zinc-100 dark:bg-zinc-800 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  </div>
)

// ── Component ─────────────────────────────
export const ChatbotGrid: React.FC<ChatbotGridProps> = ({
  limit,
  showControls = true,
  showActions = true,
  variant = 'grid',
  onDelete,
  onTest,
}) => {
  const [search, setSearch] = useState('')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [chatbotToDelete, setChatbotToDelete] = useState<Chatbot | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const { chatbots, isLoading, error } = useChatbots()
  const { deleteChatbot } = useChatbotStore()
  const navigate = useNavigate()

  const filtered = React.useMemo(() => {
    let list = [...chatbots]
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(
        c =>
          c.name.toLowerCase().includes(q) ||
          c.description?.toLowerCase().includes(q) ||
          c.industry?.toLowerCase().includes(q)
      )
    }
    return limit ? list.slice(0, limit) : list
  }, [chatbots, search, limit])

  const requestDelete = (bot: Chatbot) => {
    setChatbotToDelete(bot)
    setShowDeleteDialog(true)
  }

  const confirmDelete = async () => {
    if (!chatbotToDelete) return
    setIsDeleting(true)
    try {
      await deleteChatbot(chatbotToDelete.id)
      onDelete?.(chatbotToDelete)
      setShowDeleteDialog(false)
      setChatbotToDelete(null)
    } catch (err) {
      logger.error('Delete failed', {
        component: 'ChatbotGrid',
        error: err instanceof Error ? err.message : 'Unknown',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // ── Card ──
  const renderCard = (bot: Chatbot) => {
    const gradient = pickGradient(bot.name)
    return (
      <Card
        key={bot.id}
        className="group overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer border-border/60"
        onClick={() => navigate(`/dashboard/chatbots/${bot.id}`)}
      >
        {/* Gradient preview area with widget mockup */}
        <div className={`relative h-44 bg-gradient-to-br ${gradient.bg} overflow-hidden`}>
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_40%,white_1px,transparent_1px)] bg-[length:20px_20px]" />
          {/* Status indicator */}
          <div className="absolute top-3 left-3">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full backdrop-blur-sm ${
              bot.is_active
                ? 'bg-white/20 text-white'
                : 'bg-black/20 text-white/70'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${bot.is_active ? 'bg-emerald-400' : 'bg-white/40'}`} />
              {bot.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          {/* Model badge */}
          <div className="absolute top-3 right-3">
            <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-black/15 text-white/80 backdrop-blur-sm">
              {modelLabel(bot.ai_model)}
            </span>
          </div>
          {/* Mini widget preview */}
          <WidgetPreview name={bot.name} accent={gradient.accent} />
        </div>

        {/* Bottom section: name + meta + menu */}
        <div className="px-4 py-3.5 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-foreground text-sm truncate">
              {bot.name}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Created {relativeDate(bot.created_at)}
            </p>
          </div>

          {showActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100 outline-none shrink-0"
                  onClick={e => e.stopPropagation()}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate(`/dashboard/chatbots/${bot.id}`) }}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Playground
                </DropdownMenuItem>
                <DropdownMenuItem onClick={e => { e.stopPropagation(); onTest?.(bot) }}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Test Chat
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate(`/dashboard/chatbots/${bot.id}/data-sources`) }}>
                  <Database className="w-4 h-4 mr-2" />
                  Data Sources
                </DropdownMenuItem>
                <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate(`/dashboard/chatbots/${bot.id}/actions`) }}>
                  <Wrench className="w-4 h-4 mr-2" />
                  Actions
                </DropdownMenuItem>
                <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate(`/dashboard/chatbots/${bot.id}/analytics`) }}>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytics
                </DropdownMenuItem>
                <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate(`/dashboard/chatbots/${bot.id}/settings`) }}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={e => { e.stopPropagation(); requestDelete(bot) }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </Card>
    )
  }

  // ── Skeleton ──
  const renderSkeleton = () => (
    <div
      className={`grid gap-5 ${
        variant === 'grid'
          ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'
          : 'grid-cols-1 max-w-md'
      }`}
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} className="overflow-hidden">
          <Skeleton className="h-44 rounded-none" />
          <div className="px-4 py-3.5 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </Card>
      ))}
    </div>
  )

  // ── Empty ──
  const renderEmpty = () => (
    <div className="flex flex-col items-center justify-center py-24 px-4">
      <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-5">
        <Bot className="w-7 h-7 text-muted-foreground" />
      </div>
      <h3 className="text-base font-semibold text-foreground mb-1">
        {search ? 'No results found' : 'No agents yet'}
      </h3>
      <p className="text-sm text-muted-foreground text-center max-w-xs">
        {search
          ? `Nothing matched "${search}". Try a different search.`
          : 'Create your first AI agent to get started.'}
      </p>
    </div>
  )

  if (error) {
    return (
      <Card className="border-destructive/30 bg-destructive/5">
        <div className="p-6 text-center">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </Card>
    )
  }

  return (
    <div>
      {/* Search */}
      {showControls && chatbots.length > 0 && (
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search agents..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>
          {search && filtered.length > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {filtered.length} of {chatbots.length}
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        renderSkeleton()
      ) : filtered.length === 0 ? (
        renderEmpty()
      ) : (
        <div
          className={`grid gap-5 ${
            variant === 'grid'
              ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'
              : 'grid-cols-1 max-w-md'
          }`}
        >
          {filtered.map(renderCard)}
        </div>
      )}

      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        title={chatbotToDelete?.name || ''}
        itemType="chatbot"
        deletionItems={[
          'The chatbot and its configuration',
          'All training data and knowledge base',
          'All conversation history',
          'Widget integration settings',
          'Analytics and performance data',
          'Team assignments and permissions',
        ]}
        isDeleting={isDeleting}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false)
          setChatbotToDelete(null)
        }}
      />
    </div>
  )
}

export default ChatbotGrid
